import { githubHostOf, type NodeDTO, type ProjectDTO } from "@roomate/core";
import { useMe, useNodes, useServiceCheck } from "../api.ts";
import { Card, Pill } from "../calm.tsx";
import { Button } from "../ui.tsx";
import { githubNodeStatuses, githubService, mergingNode, nodeGithubReady, projectNeedsGithubMachine, WAITING_FOR_GITHUB } from "./github-readiness.ts";
import { ServiceRow } from "./team-nodes/NodeServices.tsx";
import { serviceChip } from "./team-nodes/service-state.ts";

type CheckProps = { canCheck: boolean; onCheck: () => void; checking: boolean; error?: string | null };

/**
 * What GitHub needs on the project page (github-machine-cli.md §5.3, §7.2): the merging machine's state with the catalog's fix
 * command, or "Waiting for a machine logged in to GitHub". Presentational. There is no token field: the login lives on the machine.
 */
export function ProjectGithubCard({ project, host, nodes, admin, canCheck, onCheck, checking, error, now = Date.now() }: { project: Pick<ProjectDTO, "id" | "homeNodeId" | "github" | "nodeChecks">; host: string; nodes: readonly NodeDTO[]; admin: boolean; now?: number } & CheckProps) {
  const node = mergingNode(project, nodes);
  const service = node ? githubService(node, host) : null;
  const ready = !!node && nodeGithubReady(node, host, now);
  const repo = project.github?.fullName ?? null;
  const access = node ? project.nodeChecks?.find((c) => c.nodeId === node.id) : undefined;
  const cannotPush = ready && access && ((access.push && !access.push.ok && access.push.result === "error") || (access.ghRepoAccess && !access.ghRepoAccess.ok));
  const message = access?.push?.message ?? access?.ghRepoAccess?.message ?? null;
  return (
    <Card pad={false} className="mb-6" testId="project-github">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-[13px]">
        <span className="font-medium text-ink">GitHub{repo ? ` · ${repo}` : ""}</span>
        {node && (
          <Button type="button" onClick={onCheck} disabled={!canCheck || checking} data-testid="project-github-check">
            {checking ? "Checking…" : "Check again"}
          </Button>
        )}
      </div>
      {!node || !service ? (
        <p className="border-t border-rule px-5 py-3 text-[13px] text-ink-2" data-testid="project-github-waiting">
          {WAITING_FOR_GITHUB}
          {node ? `. ${node.name} has not reported its GitHub login yet; press Check again.` : ""}
        </p>
      ) : (
        <ul className="divide-y divide-rule/60 border-t border-rule" data-testid="project-github-node">
          <ServiceRow service={service} machine={node.name} admin={admin} nodeId={node.id} />
        </ul>
      )}
      {error && <p className="border-t border-rule px-5 py-2 text-[12px] text-high" role="alert" data-testid="project-github-check-error">{error}</p>}
      {node && service && !ready && <p className="border-t border-rule px-5 py-2 text-[12.5px] text-ink-2" data-testid="project-github-waiting">{WAITING_FOR_GITHUB}</p>}
      {ready && !cannotPush && node && <p className="border-t border-rule px-5 py-2 text-[12.5px] text-ink-2" data-testid="project-github-ready">GitHub: ready on {node.name}</p>}
      {cannotPush && node && (
        <p className="border-t border-rule px-5 py-2 text-[12.5px] text-ink-2" data-testid="project-github-no-access">
          {node.name} is logged in{admin && service?.label ? ` as ${service.label}` : ""}, but cannot push{repo ? ` to ${repo}` : ""}.{message ? ` ${message}` : ""}
          {repo && (
            <>
              {" "}
              <a href={`https://${host}/${repo}/settings/access`} target="_blank" rel="noreferrer" className="underline decoration-rule hover:text-ink">
                Repository access ↗
              </a>
            </>
          )}
        </p>
      )}
    </Card>
  );
}

/** Project page block: only for a project whose repo lives on a GitHub host. */
export function ProjectGithub({ project }: { project: ProjectDTO }) {
  const host = githubHostOf(project.repoUrl);
  // Only a project that publishes through a machine's gh waits on one; local, remote and App-bound projects do not.
  if (!host || !projectNeedsGithubMachine(project)) return null;
  return <ProjectGithubConnected project={project} host={host} />;
}

function ProjectGithubConnected({ project, host }: { project: ProjectDTO; host: string }) {
  const me = useMe().data;
  const nodes = useNodes().data ?? [];
  const check = useServiceCheck();
  const node = mergingNode(project, nodes);
  const admin = me?.access === "admin";
  return (
    <ProjectGithubCard
      project={project}
      host={host}
      nodes={nodes}
      admin={admin}
      canCheck={!!node && (admin || node.memberId === me?.id)}
      onCheck={() => node && check.mutate(node.id)}
      checking={!!node && check.isPending && check.variables === node.id}
      error={node && check.isError && check.variables === node.id ? check.error.message : null}
    />
  );
}

/** New project dialog, for a GitHub URL: which machines can already use `gh` for it. */
export function GithubReadyNodes({ host, nodes, admin, now = Date.now() }: { host: string; nodes: readonly NodeDTO[]; admin: boolean; now?: number }) {
  const statuses = githubNodeStatuses(nodes, host, now);
  const ready = statuses.filter((s) => s.ready);
  return (
    <div className="mt-2 rounded-xl border border-rule px-3 py-2 text-[12.5px]" data-testid="new-project-github">
      <p className="text-ink-2" data-testid="new-project-github-summary">
        {statuses.length === 0
          ? "No machine is connected yet."
          : ready.length === 0
            ? WAITING_FOR_GITHUB
            : `${ready.length} of ${statuses.length} machine${statuses.length === 1 ? " is" : "s are"} ready for GitHub`}
      </p>
      {statuses.length > 0 && (
        <ul className="mt-1.5 space-y-1">
          {statuses.map(({ node, service, ready: isReady }) => {
            const chip = serviceChip(service?.state ?? "unknown");
            return (
              <li key={node.id} className="flex items-center gap-2" data-testid={`new-project-github-node-${node.id}`}>
                <span className="text-ink">{node.name}</span>
                <Pill tone={chip.tone}>{service ? chip.label : "Not reported"}</Pill>
                {isReady && admin && service?.label && <span className="text-ink-3">{service.label}</span>}
              </li>
            );
          })}
        </ul>
      )}
      <p className="mt-1.5 text-ink-3">Roomate uses the GitHub login already on the machine. Fix commands are on the Team page.</p>
    </div>
  );
}

/** Connected wrapper for the dialog; renders nothing for a URL that is not on a GitHub host. */
export function NewProjectGithub({ url }: { url: string }) {
  const host = githubHostOf(url);
  const me = useMe().data;
  const nodes = useNodes().data ?? [];
  if (!host) return null;
  return <GithubReadyNodes host={host} nodes={nodes} admin={me?.access === "admin"} />;
}
